/**
 * Support Page
 * =============
 * Public page - accessible without authentication
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, MessageCircle, Phone, Clock, HelpCircle, ChevronDown, ChevronUp, Send, ExternalLink, Search, X, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import NearULogo from '../components/NearULogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SupportPage = () => {
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [faqSearchQuery, setFaqSearchQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [ticketId, setTicketId] = useState(null);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const faqs = [
    {
      question: 'How do I reset my password?',
      answer: 'Go to the login screen and tap "Forgot Password". Enter your email address and we\'ll send you a link to reset your password. The link expires after 24 hours.'
    },
    {
      question: 'How do I delete my account?',
      answer: 'Go to Profile → Settings → Account → Delete Account. Please note that account deletion is permanent and cannot be undone. Your data will be removed in accordance with our Privacy Policy.'
    },
    {
      question: 'How does Proximity work?',
      answer: 'Proximity allows you to see other NearU members who are at the same location as you. When you enable Proximity, your current location is matched with nearby places. Other members who have also enabled Proximity at that location will appear in your "Here Now" list.'
    },
    {
      question: 'Is my location shared with other users?',
      answer: 'Your exact coordinates are never shared with other users. When you enable Proximity, other members only see the name of the place (e.g., "Starbucks on Main St"), not your precise location. You can disable Proximity at any time.'
    },
    {
      question: 'How do I report a user?',
      answer: 'Open the user\'s profile, tap the menu icon (three dots), and select "Report". Choose the reason for your report and provide any additional details. Our Trust & Safety team reviews all reports within 24 hours.'
    },
    {
      question: 'What membership tiers are available?',
      answer: 'NearU offers Free, Gold, and Diamond membership tiers. Premium tiers unlock additional features like higher gift limits, priority matching, and VIP access. Visit Profile → Manage Membership to see all available options.'
    },
    {
      question: 'How do I cancel my subscription?',
      answer: 'Go to Profile → Manage Membership and tap "Cancel Subscription". Your premium features will remain active until the end of your current billing period. You can resubscribe at any time.'
    },
    {
      question: 'I\'m having technical issues with the app',
      answer: 'Try these steps: 1) Force close and reopen the app, 2) Check for app updates, 3) Clear the app cache in your device settings, 4) Restart your device. If issues persist, contact support with details about your device and the problem.'
    }
  ];

  const contactMethods = [
    {
      icon: Mail,
      title: 'Email Support',
      description: 'Get help via email',
      value: 'info@nearuapp.net',
      link: 'mailto:info@nearuapp.net'
    },
    {
      icon: MessageCircle,
      title: 'In-App Chat',
      description: 'Available for logged-in users',
      value: 'Open in app',
      link: null
    },
    {
      icon: Phone,
      title: 'Phone Support',
      description: 'Premium members only',
      value: '+1 (800) NEARU-VIP',
      link: 'tel:+18006327847'
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/support/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(contactForm)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setSubmitStatus('success');
        setTicketId(data.ticket_id);
        setContactForm({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Failed to submit form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0D0D0D 0%, #1A0F0A 50%, #0D0D0D 100%)',
      paddingBottom: '40px'
    }}>
      {/* Header */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        background: 'rgba(26, 15, 10, 0.95)',
        backdropFilter: 'blur(10px)',
        padding: '16px 20px',
        borderBottom: '1px solid rgba(165, 124, 51, 0.2)',
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'none',
            border: 'none',
            color: '#E6D7C3',
            cursor: 'pointer',
            padding: '8px',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <ArrowLeft size={24} />
        </button>
        <NearULogo size="small" showText={true} />
      </div>

      <div style={{ padding: '24px 20px', maxWidth: '800px', margin: '0 auto' }}>
        {/* Title */}
        <h1 style={{
          fontFamily: 'Cinzel, serif',
          fontSize: '28px',
          fontWeight: '600',
          color: '#E6D7C3',
          marginBottom: '8px',
          textAlign: 'center'
        }}>
          Help & Support
        </h1>
        
        <p style={{
          color: '#A57C33',
          fontSize: '14px',
          textAlign: 'center',
          marginBottom: '32px'
        }}>
          We're here to help you
        </p>

        {/* Response Time Banner */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(76, 175, 80, 0.05) 100%)',
          borderRadius: '12px',
          padding: '16px 20px',
          marginBottom: '32px',
          border: '1px solid rgba(76, 175, 80, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <Clock size={24} color="#4CAF50" />
          <div>
            <div style={{ color: '#4CAF50', fontWeight: '600', fontSize: '14px' }}>
              Average Response Time
            </div>
            <div style={{ color: '#A57C33', fontSize: '13px' }}>
              We typically respond within 24-48 hours
            </div>
          </div>
        </div>

        {/* Contact Methods */}
        <h2 style={{
          fontFamily: 'Cinzel, serif',
          fontSize: '20px',
          color: '#E6D7C3',
          marginBottom: '16px'
        }}>
          Contact Us
        </h2>

        <div style={{ display: 'grid', gap: '12px', marginBottom: '32px' }}>
          {contactMethods.map((method, index) => {
            const Icon = method.icon;
            return (
              <a
                key={index}
                href={method.link || '#'}
                onClick={method.link ? undefined : (e) => e.preventDefault()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  padding: '16px 20px',
                  background: 'rgba(26, 15, 10, 0.6)',
                  borderRadius: '12px',
                  border: '1px solid rgba(165, 124, 51, 0.15)',
                  textDecoration: 'none',
                  cursor: method.link ? 'pointer' : 'default'
                }}
              >
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, rgba(215, 181, 109, 0.2) 0%, rgba(165, 124, 51, 0.1) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Icon size={24} color="#D7B56D" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#E6D7C3', fontWeight: '600', fontSize: '15px' }}>
                    {method.title}
                  </div>
                  <div style={{ color: '#8A7355', fontSize: '13px' }}>
                    {method.description}
                  </div>
                </div>
                <div style={{ color: '#D7B56D', fontSize: '14px', fontWeight: '500' }}>
                  {method.value}
                </div>
                {method.link && <ExternalLink size={16} color="#A57C33" />}
              </a>
            );
          })}
        </div>

        {/* FAQ Section */}
        <h2 style={{
          fontFamily: 'Cinzel, serif',
          fontSize: '20px',
          color: '#E6D7C3',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <HelpCircle size={24} color="#D7B56D" />
          Frequently Asked Questions
        </h2>

        {/* FAQ Search */}
        <div style={{
          position: 'relative',
          marginBottom: '20px'
        }}>
          <Search 
            size={18} 
            color="#8A7355" 
            style={{
              position: 'absolute',
              left: '16px',
              top: '50%',
              transform: 'translateY(-50%)'
            }}
          />
          <input
            type="text"
            placeholder="Search FAQs..."
            value={faqSearchQuery}
            onChange={(e) => setFaqSearchQuery(e.target.value)}
            data-testid="faq-search-input"
            style={{
              width: '100%',
              padding: '14px 44px',
              background: 'rgba(26, 15, 10, 0.6)',
              border: '1px solid rgba(165, 124, 51, 0.2)',
              borderRadius: '12px',
              color: '#E6D7C3',
              fontSize: '14px',
              boxSizing: 'border-box',
              outline: 'none'
            }}
          />
          {faqSearchQuery && (
            <button
              onClick={() => setFaqSearchQuery('')}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(165, 124, 51, 0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
              data-testid="faq-search-clear"
            >
              <X size={14} color="#A57C33" />
            </button>
          )}
        </div>

        {/* Filtered FAQs */}
        <div style={{ marginBottom: '32px' }}>
          {faqs
            .filter(faq => 
              faqSearchQuery === '' ||
              faq.question.toLowerCase().includes(faqSearchQuery.toLowerCase()) ||
              faq.answer.toLowerCase().includes(faqSearchQuery.toLowerCase())
            )
            .map((faq, index) => {
              const originalIndex = faqs.findIndex(f => f.question === faq.question);
              return (
            <div
              key={originalIndex}
              style={{
                background: 'rgba(26, 15, 10, 0.6)',
                borderRadius: '12px',
                marginBottom: '8px',
                border: '1px solid rgba(165, 124, 51, 0.15)',
                overflow: 'hidden'
              }}
            >
              <button
                onClick={() => setExpandedFaq(expandedFaq === originalIndex ? null : originalIndex)}
                style={{
                  width: '100%',
                  padding: '16px 20px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  textAlign: 'left'
                }}
              >
                <span style={{ color: '#E6D7C3', fontSize: '14px', fontWeight: '500', flex: 1, paddingRight: '12px' }}>
                  {faq.question}
                </span>
                {expandedFaq === originalIndex ? (
                  <ChevronUp size={20} color="#A57C33" />
                ) : (
                  <ChevronDown size={20} color="#A57C33" />
                )}
              </button>
              {expandedFaq === originalIndex && (
                <div style={{
                  padding: '0 20px 16px',
                  color: '#A57C33',
                  fontSize: '14px',
                  lineHeight: '1.6'
                }}>
                  {faq.answer}
                </div>
              )}
            </div>
          );
          })}
          
          {/* No results message */}
          {faqSearchQuery && faqs.filter(faq => 
            faq.question.toLowerCase().includes(faqSearchQuery.toLowerCase()) ||
            faq.answer.toLowerCase().includes(faqSearchQuery.toLowerCase())
          ).length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '24px',
              color: '#8A7355',
              fontSize: '14px'
            }}>
              No FAQs found matching "{faqSearchQuery}"
            </div>
          )}
        </div>

        {/* Contact Form */}
        <h2 style={{
          fontFamily: 'Cinzel, serif',
          fontSize: '20px',
          color: '#E6D7C3',
          marginBottom: '16px'
        }}>
          Send Us a Message
        </h2>

        <form onSubmit={handleSubmit} style={{
          background: 'rgba(26, 15, 10, 0.6)',
          borderRadius: '16px',
          padding: '24px',
          border: '1px solid rgba(165, 124, 51, 0.15)'
        }}>
          <div style={{ display: 'grid', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', color: '#A57C33', fontSize: '13px', marginBottom: '6px' }}>
                Your Name
              </label>
              <input
                type="text"
                value={contactForm.name}
                onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                required
                disabled={isSubmitting}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(165, 124, 51, 0.3)',
                  borderRadius: '8px',
                  color: '#E6D7C3',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  opacity: isSubmitting ? 0.6 : 1
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', color: '#A57C33', fontSize: '13px', marginBottom: '6px' }}>
                Email Address
              </label>
              <input
                type="email"
                value={contactForm.email}
                onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                required
                disabled={isSubmitting}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(165, 124, 51, 0.3)',
                  borderRadius: '8px',
                  color: '#E6D7C3',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  opacity: isSubmitting ? 0.6 : 1
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', color: '#A57C33', fontSize: '13px', marginBottom: '6px' }}>
                Subject
              </label>
              <select
                value={contactForm.subject}
                onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                required
                disabled={isSubmitting}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(165, 124, 51, 0.3)',
                  borderRadius: '8px',
                  color: '#E6D7C3',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  opacity: isSubmitting ? 0.6 : 1
                }}
              >
                <option value="">Select a topic...</option>
                <option value="account">Account Issues</option>
                <option value="billing">Billing & Payments</option>
                <option value="technical">Technical Problems</option>
                <option value="safety">Safety Concern</option>
                <option value="feedback">Feedback & Suggestions</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', color: '#A57C33', fontSize: '13px', marginBottom: '6px' }}>
                Message
              </label>
              <textarea
                value={contactForm.message}
                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                required
                disabled={isSubmitting}
                rows={5}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(165, 124, 51, 0.3)',
                  borderRadius: '8px',
                  color: '#E6D7C3',
                  fontSize: '14px',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                  opacity: isSubmitting ? 0.6 : 1
                }}
              />
            </div>
            
            {/* Success Message */}
            {submitStatus === 'success' && (
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                padding: '16px',
                background: 'rgba(76, 175, 80, 0.15)',
                border: '1px solid rgba(76, 175, 80, 0.3)',
                borderRadius: '10px'
              }}>
                <CheckCircle size={20} color="#4CAF50" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <div style={{ color: '#4CAF50', fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>
                    Message Sent Successfully!
                  </div>
                  <div style={{ color: '#A57C33', fontSize: '13px', lineHeight: '1.5' }}>
                    Thank you for contacting us. We'll respond within 24-48 hours.
                    {ticketId && (
                      <span style={{ display: 'block', marginTop: '4px', color: '#8A7355' }}>
                        Reference: {ticketId}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Error Message */}
            {submitStatus === 'error' && (
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                padding: '16px',
                background: 'rgba(244, 67, 54, 0.15)',
                border: '1px solid rgba(244, 67, 54, 0.3)',
                borderRadius: '10px'
              }}>
                <AlertCircle size={20} color="#F44336" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <div style={{ color: '#F44336', fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>
                    Failed to Send Message
                  </div>
                  <div style={{ color: '#A57C33', fontSize: '13px', lineHeight: '1.5' }}>
                    Please try again or email us directly at info@nearuapp.net
                  </div>
                </div>
              </div>
            )}
            
            <button
              type="submit"
              disabled={isSubmitting}
              data-testid="contact-form-submit"
              style={{
                width: '100%',
                padding: '14px',
                background: isSubmitting 
                  ? 'rgba(165, 124, 51, 0.3)' 
                  : 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)',
                border: 'none',
                borderRadius: '10px',
                color: isSubmitting ? '#8A7355' : '#1A0F0A',
                fontSize: '15px',
                fontWeight: '600',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                opacity: isSubmitting ? 0.7 : 1
              }}
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Send Message
                </>
              )}
            </button>
          </div>
          
          <style>{`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
        </form>
      </div>
    </div>
  );
};

export default SupportPage;
